import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Tag, Modal, Form, Select, Input, DatePicker, Space, Popconfirm, message, Row, Col, Statistic, Avatar } from 'antd';
import { SearchOutlined, EditOutlined, DeleteOutlined, ExportOutlined, FilterOutlined, UserOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface User {
  key: string;
  id: string;
  phone: string;
  nickname: string;
  email: string;
  idCard: string;
  licenseNumber: string;
  city: string;
  isVerified: boolean;
  status: 'active' | 'blocked' | 'pending';
  totalOrders: number;
  totalSpent: number;
  registeredAt: string;
  lastLoginAt: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [rechargeModalVisible, setRechargeModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [rechargeForm] = Form.useForm();
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);

  // サンプルデータ
  const mockUsers: User[] = [
    {
      key: '1',
      id: 'U001',
      phone: '090-1234-5678',
      nickname: '田中太郎',
      email: 'tanaka@example.com',
      idCard: '110101199001011234',
      licenseNumber: 'C1110101199001011234',
      city: '東京',
      isVerified: true,
      status: 'active',
      totalOrders: 15,
      totalSpent: 268000,
      registeredAt: '2023-12-01 10:30:00',
      lastLoginAt: '2024-02-01 14:25:00',
    },
    {
      key: '2',
      id: 'U002',
      phone: '080-9876-5432',
      nickname: '佐藤花子',
      email: 'sato@example.com',
      idCard: '110101199002021234',
      licenseNumber: '',
      city: '大阪',
      isVerified: false,
      status: 'pending',
      totalOrders: 3,
      totalSpent: 45600,
      registeredAt: '2024-01-15 16:20:00',
      lastLoginAt: '2024-01-30 09:15:00',
    },
    {
      key: '3',
      id: 'U003',
      phone: '070-5555-7777',
      nickname: '山田一郎',
      email: 'yamada@example.com',
      idCard: '110101199003031234',
      licenseNumber: 'C1110101199003031234',
      city: '名古屋',
      isVerified: true,
      status: 'active',
      totalOrders: 28,
      totalSpent: 524000,
      registeredAt: '2023-10-20 11:45:00',
      lastLoginAt: '2024-02-02 08:30:00',
    },
    {
      key: '4',
      id: 'U004',
      phone: '090-8888-1111',
      nickname: '鈴木次郎',
      email: 'suzuki@example.com',
      idCard: '110101199004041234',
      licenseNumber: 'C1110101199004041234',
      city: '福岡',
      isVerified: true,
      status: 'blocked',
      totalOrders: 7,
      totalSpent: 112000,
      registeredAt: '2023-11-10 15:15:00',
      lastLoginAt: '2024-01-20 19:45:00',
    },
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = () => {
    setLoading(true);
    // サンプルAPI呼び出し
    setTimeout(() => {
      setUsers(mockUsers);
      setFilteredUsers(mockUsers);
      setLoading(false);
    }, 500);
  };

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      active: 'green',
      blocked: 'red',
      pending: 'orange',
    };
    return colorMap[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const textMap: Record<string, string> = {
      active: 'アクティブ',
      blocked: '利用停止',
      pending: '承認待ち',
    };
    return textMap[status] || status;
  };

  const handleSearch = (values: any) => {
    let filtered = users;
    
    if (values.phone) {
      filtered = filtered.filter(user => 
        user.phone.includes(values.phone)
      );
    }
    
    if (values.nickname) {
      filtered = filtered.filter(user => 
        user.nickname.includes(values.nickname)
      );
    }
    
    if (values.city) {
      filtered = filtered.filter(user => user.city === values.city);
    }
    
    if (values.status) {
      filtered = filtered.filter(user => user.status === values.status);
    }
    
    if (values.isVerified !== undefined) {
      filtered = filtered.filter(user => user.isVerified === values.isVerified);
    }
    
    if (values.dateRange && values.dateRange.length === 2) {
      const [startDate, endDate] = values.dateRange;
      filtered = filtered.filter(user => {
        const userDate = dayjs(user.registeredAt);
        return userDate.isAfter(startDate) && userDate.isBefore(endDate);
      });
    }
    
    setFilteredUsers(filtered);
    message.success(`${filtered.length}件のユーザー情報が見つかりました`);
  };

  const handleEdit = (record: User) => {
    setSelectedUser(record);
    form.setFieldsValue(record);
    setEditModalVisible(true);
  };

  const handleViewDetail = (record: User) => {
    setSelectedUser(record);
    setDetailModalVisible(true);
  };

  const handleRecharge = (record: User) => {
    setSelectedUser(record);
    rechargeForm.resetFields();
    setRechargeModalVisible(true);
  };

  const handleBlock = (userId: string, action: 'block' | 'unblock') => {
    const newStatus = action === 'block' ? 'blocked' : 'active';
    const updatedUsers = users.map(user => 
      user.id === userId ? { ...user, status: newStatus as User['status'] } : user
    );
    
    setUsers(updatedUsers);
    setFilteredUsers(updatedUsers);
    message.success(action === 'block' ? 'ユーザーを利用停止しました' : 'ユーザーの利用を再開しました');
  };

  const handleDelete = (userId: string) => {
    const newUsers = users.filter(user => user.id !== userId);
    setUsers(newUsers);
    setFilteredUsers(newUsers);
    message.success('ユーザーを削除しました');
  };

  const handleUpdateUser = (values: any) => {
    if (!selectedUser) return;
    
    const updatedUsers = users.map(user => 
      user.id === selectedUser.id ? { ...user, ...values } : user
    );
    
    setUsers(updatedUsers);
    setFilteredUsers(updatedUsers);
    setEditModalVisible(false);
    message.success('ユーザー情報を更新しました');
  };

  const handleRechargeSubmit = (values: any) => {
    message.success(`ユーザー ${selectedUser?.nickname} 様に¥${values.amount}をチャージしました`);
    setRechargeModalVisible(false);
  };

  const handleExport = () => {
    message.success('ユーザーデータをエクスポートしました');
  };

  const calculateStatistics = () => {
    const totalUsers = filteredUsers.length;
    const activeUsers = filteredUsers.filter(user => user.status === 'active').length;
    const verifiedUsers = filteredUsers.filter(user => user.isVerified).length;
    const totalRevenue = filteredUsers.reduce((sum, user) => sum + user.totalSpent, 0);
    
    return {
      totalUsers,
      activeUsers,
      verifiedUsers,
      totalRevenue,
    };
  };

  const statistics = calculateStatistics();

  const columns: ColumnsType<User> = [
    {
      title: 'ユーザー情報',
      key: 'userInfo',
      width: 180,
      fixed: 'left',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar icon={<UserOutlined />} style={{ marginRight: 8 }} />
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.nickname}</div>
            <div style={{ fontSize: 12, color: '#666' }}>{record.phone}</div>
          </div>
        </div>
      ),
    },
    {
      title: '認証状況',
      key: 'verification',
      width: 120,
      render: (_, record) => (
        <div>
          <Tag color={record.isVerified ? 'green' : 'orange'}>
            {record.isVerified ? '本人確認済み' : '本人未確認'}
          </Tag>
          <br />
          <Tag color={getStatusColor(record.status)} style={{ marginTop: 4 }}>
            {getStatusText(record.status)}
          </Tag>
        </div>
      ),
    },
    {
      title: '連絡先情報',
      key: 'contact',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: 12 }}>メール: {record.email || '未設定'}</div>
          <div style={{ fontSize: 12 }}>居住地: {record.city}</div>
          <div style={{ fontSize: 12 }}>身元証明: {record.idCard ? `${record.idCard.slice(0, 6)}****${record.idCard.slice(-4)}` : '未設定'}</div>
        </div>
      ),
    },
    {
      title: '運転免許証',
      dataIndex: 'licenseNumber',
      key: 'licenseNumber',
      width: 150,
      render: (text) => text || <span style={{ color: '#ccc' }}>未登録</span>,
    },
    {
      title: '統計データ',
      key: 'stats',
      width: 120,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: 12 }}>利用回数: {record.totalOrders}</div>
          <div style={{ fontSize: 12 }}>利用金額: ¥{record.totalSpent}</div>
        </div>
      ),
    },
    {
      title: '登録日時',
      dataIndex: 'registeredAt',
      key: 'registeredAt',
      width: 120,
      render: (text) => dayjs(text).format('YYYY-MM-DD'),
    },
    {
      title: '最終ログイン',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      width: 120,
      render: (text) => dayjs(text).format('MM-DD HH:mm'),
    },
    {
      title: '操作',
      key: 'action',
      width: 250,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => handleViewDetail(record)}
          >
            詳細
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            編集
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => handleRecharge(record)}
          >
            チャージ
          </Button>
          {record.status === 'active' ? (
            <Popconfirm
              title="このユーザーを利用停止しますか？"
              onConfirm={() => handleBlock(record.id, 'block')}
              okText="確定"
              cancelText="キャンセル"
            >
              <Button type="link" size="small" danger>
                利用停止
              </Button>
            </Popconfirm>
          ) : record.status === 'blocked' ? (
            <Button
              type="link"
              size="small"
              onClick={() => handleBlock(record.id, 'unblock')}
            >
              利用再開
            </Button>
          ) : null}
          <Popconfirm
            title="このユーザーを削除しますか？"
            onConfirm={() => handleDelete(record.id)}
            okText="確定"
            cancelText="キャンセル"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              削除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <h1 style={{ marginBottom: 24 }}>ユーザー管理</h1>
      
      {/* 統計カード */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="総ユーザー数" value={statistics.totalUsers} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="アクティブユーザー" value={statistics.activeUsers} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="本人確認済みユーザー" value={statistics.verifiedUsers} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="総売上" value={statistics.totalRevenue} prefix="¥" />
          </Card>
        </Col>
      </Row>

      {/* 検索フォーム */}
      <Card style={{ marginBottom: 16 }}>
        <Form
          form={searchForm}
          onFinish={handleSearch}
          layout="inline"
        >
          <Form.Item name="phone">
            <Input placeholder="電話番号" prefix={<SearchOutlined />} />
          </Form.Item>
          <Form.Item name="nickname">
            <Input placeholder="ユーザー名" />
          </Form.Item>
          <Form.Item name="city">
            <Select placeholder="地域" style={{ width: 100 }}>
              <Option value="">全て</Option>
              <Option value="東京">東京</Option>
              <Option value="大阪">大阪</Option>
              <Option value="名古屋">名古屋</Option>
              <Option value="福岡">福岡</Option>
            </Select>
          </Form.Item>
          <Form.Item name="status">
            <Select placeholder="状況" style={{ width: 100 }}>
              <Option value="">全て</Option>
              <Option value="active">アクティブ</Option>
              <Option value="blocked">利用停止</Option>
              <Option value="pending">承認待ち</Option>
            </Select>
          </Form.Item>
          <Form.Item name="isVerified">
            <Select placeholder="認証状況" style={{ width: 100 }}>
              <Option value={undefined}>全て</Option>
              <Option value={true}>本人確認済み</Option>
              <Option value={false}>本人未確認</Option>
            </Select>
          </Form.Item>
          <Form.Item name="dateRange">
            <RangePicker placeholder={['開始日時', '終了日時']} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<FilterOutlined />}>
                検索
              </Button>
              <Button onClick={() => {
                searchForm.resetFields();
                setFilteredUsers(users);
              }}>
                リセット
              </Button>
              <Button icon={<ExportOutlined />} onClick={handleExport}>
                エクスポート
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* ユーザーテーブル */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredUsers}
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            total: filteredUsers.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]}件 / 全${total}件`,
          }}
        />
      </Card>

      {/* ユーザー編集モーダル */}
      <Modal
        title="ユーザー編集"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        width={600}
        footer={null}
      >
        <Form
          form={form}
          onFinish={handleUpdateUser}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="nickname"
                label="ユーザー名"
                rules={[{ required: true, message: 'ユーザー名を入力してください' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="電話番号"
                rules={[{ required: true, message: '電話番号を入力してください' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="email" label="メールアドレス">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="city" label="地域">
                <Select>
                  <Option value="東京">東京</Option>
                  <Option value="大阪">大阪</Option>
                  <Option value="名古屋">名古屋</Option>
                  <Option value="福岡">福岡</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="status" label="ユーザー状況">
                <Select>
                  <Option value="active">アクティブ</Option>
                  <Option value="blocked">利用停止</Option>
                  <Option value="pending">承認待ち</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="isVerified" label="認証状況" valuePropName="checked">
                <Select>
                  <Option value={true}>本人確認済み</Option>
                  <Option value={false}>本人未確認</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setEditModalVisible(false)}>
                キャンセル
              </Button>
              <Button type="primary" htmlType="submit">
                保存
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* ユーザー詳細モーダル */}
      <Modal
        title="ユーザー詳細情報"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={700}
        footer={null}
      >
        {selectedUser && (
          <div>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={24}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                  <Avatar size={64} icon={<UserOutlined />} style={{ marginRight: 16 }} />
                  <div>
                    <h3>{selectedUser.nickname}</h3>
                    <p style={{ margin: 0, color: '#666' }}>
                      ユーザーID: {selectedUser.id} | {selectedUser.phone}
                    </p>
                  </div>
                </div>
              </Col>
            </Row>
            
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <strong>メールアドレス：</strong>{selectedUser.email || '未設定'}
              </Col>
              <Col span={8}>
                <strong>地域：</strong>{selectedUser.city}
              </Col>
              <Col span={8}>
                <strong>登録日時：</strong>{dayjs(selectedUser.registeredAt).format('YYYY-MM-DD')}
              </Col>
            </Row>
            
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <strong>身元証明書：</strong>
                {selectedUser.idCard ? `${selectedUser.idCard.slice(0, 6)}****${selectedUser.idCard.slice(-4)}` : '未設定'}
              </Col>
              <Col span={8}>
                <strong>運転免許証：</strong>{selectedUser.licenseNumber || '未登録'}
              </Col>
              <Col span={8}>
                <strong>最終ログイン：</strong>{dayjs(selectedUser.lastLoginAt).format('YYYY-MM-DD HH:mm')}
              </Col>
            </Row>
            
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <strong>認証状況：</strong>
                <Tag color={selectedUser.isVerified ? 'green' : 'orange'} style={{ marginLeft: 8 }}>
                  {selectedUser.isVerified ? '本人確認済み' : '本人未確認'}
                </Tag>
              </Col>
              <Col span={8}>
                <strong>ユーザー状況：</strong>
                <Tag color={getStatusColor(selectedUser.status)} style={{ marginLeft: 8 }}>
                  {getStatusText(selectedUser.status)}
                </Tag>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={8}>
                <strong>総利用回数：</strong>{selectedUser.totalOrders}
              </Col>
              <Col span={8}>
                <strong>総利用金額：</strong>¥{selectedUser.totalSpent}
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* チャージモーダル */}
      <Modal
        title="ユーザーチャージ"
        open={rechargeModalVisible}
        onCancel={() => setRechargeModalVisible(false)}
        footer={null}
      >
        <Form
          form={rechargeForm}
          onFinish={handleRechargeSubmit}
          layout="vertical"
        >
          <div style={{ marginBottom: 16, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
            <strong>ユーザー情報：</strong>
            {selectedUser && (
              <div>
                <p style={{ margin: '8px 0' }}>ユーザー：{selectedUser.nickname}</p>
                <p style={{ margin: '8px 0' }}>電話番号：{selectedUser.phone}</p>
              </div>
            )}
          </div>
          
          <Form.Item
            name="amount"
            label="チャージ金額"
            rules={[
              { required: true, message: 'チャージ金額を入力してください' },
              { type: 'number', min: 1, message: '金額は1円以上で入力してください' }
            ]}
          >
            <Input prefix="¥" type="number" placeholder="チャージ金額を入力" />
          </Form.Item>
          
          <Form.Item name="remark" label="備考">
            <Input.TextArea placeholder="チャージの備考を入力" rows={3} />
          </Form.Item>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setRechargeModalVisible(false)}>
                キャンセル
              </Button>
              <Button type="primary" htmlType="submit">
                チャージ実行
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;